/**
 * Pure step decision for /bousala/smart.
 *
 * The buyer pays FIRST and takes the free Bousala afterwards, so the attempt
 * status never blocks checkout. It is kept as an input only for callers that
 * want to reason about it; the decision deliberately ignores it.
 */
export type SmartSessionKind = "none" | "anonymous" | "email";

export type SmartEntryStep = "loading" | "order_status" | "add_email" | "checkout";

export function smartEntryStep(input: {
  /** none = no Supabase session at all, anonymous = session without an email. */
  session: SmartSessionKind;
  /** getSmartPortalState().kind, or null while it has not resolved yet. */
  portalKind: string | null;
  portalLoading?: boolean;
}): SmartEntryStep {
  // No session → nothing to look up; collect the email first (the anonymous
  // session is created only when that form is submitted).
  if (input.session === "none") return "add_email";
  // An anonymous session can't own a Smart order or a pending transfer (both
  // need an email), so never wait on the portal query: returning "loading"
  // here would unmount the email form mid-submit, right after the anonymous
  // session is created. Only a resolved non-"none" kind can override this.
  if (input.session === "anonymous") {
    return input.portalKind && input.portalKind !== "none" ? "order_status" : "add_email";
  }
  if (input.portalLoading || input.portalKind === null) return "loading";
  // Any existing order OR a pending/rejected transfer → status card, so a
  // buyer can never submit a second receipt.
  if (input.portalKind !== "none") return "order_status";
  return "checkout";
}

/**
 * Where a buyer goes when their free Bousala report is ready: Smart buyers
 * continue on /portal/smart, everyone else opens their free report.
 */
export function smartFinishDestination(portalKind: string | null): "smart" | "report" {
  return portalKind && portalKind !== "none" ? "smart" : "report";
}
